// Hero pennants (dominion-path visuals, M3): a small standard on a pole next
// to each visible hero unit, its cloth colored by the owner's banner path
// (see banners.ts). Two instanced meshes — pole (shared grey) and cloth
// (per-instance color) — following the same batch pattern as everything
// else in the render layer.

import type { SceneNode } from '@aod/protocol';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { bannerFor } from './banners.js';

const POLE_HEIGHT = 0.5;
const poleGeo = new THREE.BoxGeometry(0.018, POLE_HEIGHT, 0.018).translate(0, POLE_HEIGHT / 2, 0);
// Cloth hangs off the pole's right side, top flush with the pole top.
const clothGeo = new THREE.PlaneGeometry(0.14, 0.09).translate(0.088, POLE_HEIGHT - 0.045, 0);
const poleMat = new THREE.MeshBasicMaterial({ color: '#4a4238' });
const clothMat = new THREE.MeshBasicMaterial({ color: 'rgb(255,255,255)', side: THREE.DoubleSide });

export interface HeroPennant {
  readonly unitId: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly cloth: string;
}

/** Hero pennant placements for the visible units, given the city archetypes. */
export function heroPennants(
  units: ReadonlyArray<SceneNode>,
  cities: ReadonlyArray<SceneNode>,
  visibleCoords: ReadonlySet<string> | null | undefined,
): HeroPennant[] {
  const cityRecords = cities.map((c) => (c.props ?? {}) as Record<string, unknown>);
  const out: HeroPennant[] = [];
  for (const unit of units) {
    const props = (unit.props ?? {}) as Record<string, unknown>;
    if (!props.hero) continue;
    const q = props.q as number | undefined;
    const r = props.r as number | undefined;
    if (visibleCoords && q !== undefined && r !== undefined && !visibleCoords.has(`${q},${r}`)) {
      continue;
    }
    const banner = bannerFor(props.ownerId as number | undefined, cityRecords);
    if (!banner) continue;
    const [x, y, z] = unit.transform.position;
    out.push({ unitId: unit.id, x: x + 0.2, y, z: z + 0.14, cloth: banner.cloth });
  }
  return out;
}

export function HeroPennants({
  units,
  cities,
  visibleCoords,
}: {
  readonly units: ReadonlyArray<SceneNode>;
  readonly cities: ReadonlyArray<SceneNode>;
  readonly visibleCoords?: ReadonlySet<string> | null;
}) {
  const pennants = useMemo(
    () => heroPennants(units, cities, visibleCoords),
    [units, cities, visibleCoords],
  );
  const poleRef = useRef<THREE.InstancedMesh>(null);
  const clothRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    if (!poleRef.current || !clothRef.current) return;
    const obj = new THREE.Object3D();
    const col = new THREE.Color();
    pennants.forEach((p, i) => {
      obj.position.set(p.x, p.y, p.z);
      obj.rotation.set(0, 0, 0);
      obj.scale.setScalar(1);
      obj.updateMatrix();
      poleRef.current!.setMatrixAt(i, obj.matrix);
      clothRef.current!.setMatrixAt(i, obj.matrix);
      clothRef.current!.setColorAt(i, col.set(p.cloth));
    });
    poleRef.current.instanceMatrix.needsUpdate = true;
    clothRef.current.instanceMatrix.needsUpdate = true;
    if (clothRef.current.instanceColor) clothRef.current.instanceColor.needsUpdate = true;
  }, [pennants]);

  if (pennants.length === 0) return null;
  return (
    <group>
      <instancedMesh
        args={[poleGeo, poleMat, pennants.length]}
        ref={poleRef}
        frustumCulled={false}
      />
      <instancedMesh
        args={[clothGeo, clothMat, pennants.length]}
        ref={clothRef}
        frustumCulled={false}
      />
    </group>
  );
}
